/**
 * `Site_Tracker` — the SPA's in-house engagement recorder.
 *
 * Framework-free on purpose: every browser surface it touches
 * (`sessionStorage`, `navigator.sendBeacon`, `fetch`, the clock, the random id
 * source, the screen width, the Global Privacy Control signal) arrives through
 * {@link SiteTrackerDeps}, so the queueing/batching rules are unit- and
 * property-testable with no DOM and no React. `use-site-tracker.ts` supplies the
 * real browser implementations and wires the call sites.
 *
 * Privacy shape (Req 3.1, 3.2, 3.10):
 *   - session identity is a random id in `sessionStorage`, which the browser
 *     discards with the tab; no cookie is read or written anywhere in here;
 *   - a payload carries `sessionId` plus, per event, `eventId`, `type`,
 *     `targetId`, `referrerOrigin`, `clientTimestamp` and `screenWidthBucket`,
 *     and nothing else;
 *   - the tracker disables itself entirely when the browser reports Global
 *     Privacy Control, or when no API origin is configured (Req 3.3, 3.5).
 *
 * Send timing (Req 2.8, 2.10): sends happen only when the queue reaches
 * {@link SITE_TRACKER_BATCH_SIZE} pending events, or when a caller flushes
 * (which `use-site-tracker.ts` does on `visibilitychange` → hidden, Req 2.9).
 * There is no timer and no other trigger.
 *
 * Failure shape (Req 2.12, 14.5): measurement is never allowed to affect the
 * visitor. Every public method swallows everything it can throw, a flush clears
 * the queue whatever the outcome, nothing is retried and no message is
 * produced.
 */

import {
  EngagementEventType,
  ScreenWidthBucket,
  type EngagementBatch,
  type EngagementEventInput,
} from "@photography-booking/shared";

/** `sessionStorage` key holding the per-tab session identifier (Req 3.1). */
export const SITE_TRACKER_SESSION_KEY = "hm.analytics.sid";

/** Pending-event count that triggers a send (Req 2.8). */
export const SITE_TRACKER_BATCH_SIZE = 20;

/** Path the batches are posted to, always on the configured API origin. */
export const SITE_TRACKER_PATH = "/analytics/events";

/** Screen-width bucket boundaries in CSS pixels: `sm` < 768 <= `md` < 1200. */
export const SCREEN_WIDTH_BREAKPOINTS = { md: 768, lg: 1200 } as const;

/**
 * The browser surfaces the tracker needs. Injecting them keeps the class pure
 * enough to drive from a test with plain functions and a `Map`.
 */
export interface SiteTrackerDeps {
  /** API origin; when empty or absent the tracker is disabled (Req 3.5). */
  apiBaseUrl: string;
  /** Backed by `window.sessionStorage`. */
  sessionStore: Pick<Storage, "getItem" | "setItem">;
  /** Backed by `navigator.sendBeacon`; `false` means "not sent". */
  send: (url: string, body: string) => boolean;
  now: () => number;
  randomId: () => string;
  screenWidth: () => number;
  /** `navigator.globalPrivacyControl === true` (Req 3.3). */
  privacyControlEnabled: () => boolean;
  /**
   * Fallback used when {@link send} is unavailable or reports failure —
   * `fetch(url, { keepalive: true })` by default, which also survives unload.
   * Injectable so a test never needs a real `fetch`.
   */
  sendFallback?: (url: string, body: string) => void;
  /**
   * Referrer origin of the current page load (`document.referrer`'s origin, or
   * `""` for a direct visit). Attached to page-view events only (Req 2.1).
   */
  referrerOrigin?: () => string;
}

/** Maps a viewport width to the coarse bucket that is the only device signal. */
export function screenWidthBucketOf(width: number): ScreenWidthBucket {
  if (!Number.isFinite(width) || width < SCREEN_WIDTH_BREAKPOINTS.md) {
    return ScreenWidthBucket.SM;
  }
  return width < SCREEN_WIDTH_BREAKPOINTS.lg
    ? ScreenWidthBucket.MD
    : ScreenWidthBucket.LG;
}

/**
 * Queues engagement events and posts them in batches to
 * `${apiBaseUrl}/analytics/events`.
 *
 * A disabled tracker (privacy control on, or no API origin) accepts calls and
 * does nothing at all: no queueing, no session id, no send.
 */
export class SiteTracker {
  /** False when Global Privacy Control is on or no API origin is configured. */
  readonly enabled: boolean;

  /** Stable per-tab session id; `""` when the tracker is disabled. */
  readonly sessionId: string;

  private readonly deps: SiteTrackerDeps;

  private readonly url: string;

  private readonly queue: EngagementEventInput[] = [];

  /** Monotonic per-session counter behind the unique event ids (Req 2.7). */
  private eventCounter = 0;

  /** Guards the one-per-session booking-form-started event (Req 2.5). */
  private formStartedRecorded = false;

  constructor(deps: SiteTrackerDeps) {
    this.deps = deps;
    const apiBaseUrl = normalizeBaseUrl(deps.apiBaseUrl);
    this.url = `${apiBaseUrl}${SITE_TRACKER_PATH}`;
    this.enabled = Boolean(apiBaseUrl) && !readPrivacyControl(deps);
    this.sessionId = this.enabled ? resolveSessionId(deps) : "";
  }

  /** Number of events waiting to be sent — exposed for tests. */
  get pendingCount(): number {
    return this.queue.length;
  }

  /**
   * Queues one engagement event, sending the batch as soon as
   * {@link SITE_TRACKER_BATCH_SIZE} events are pending. A page-view event also
   * carries the referrer origin (Req 2.1).
   */
  record(type: EngagementEventType, targetId?: string): void {
    if (!this.enabled) {
      return;
    }
    try {
      this.queue.push(this.buildEvent(type, targetId));
    } catch {
      // A failure to even build an event must not reach the visitor.
      return;
    }
    if (this.queue.length >= SITE_TRACKER_BATCH_SIZE) {
      this.flush();
    }
  }

  /**
   * Records the first booking-form edit of the session, and only the first:
   * later calls are no-ops however many fields the visitor touches (Req 2.5).
   */
  recordFormStarted(): void {
    if (!this.enabled || this.formStartedRecorded) {
      return;
    }
    this.formStartedRecorded = true;
    this.record(EngagementEventType.BOOKING_FORM_STARTED);
  }

  /**
   * Sends everything pending in one request and clears the queue, whatever
   * happens. `sendBeacon` first (it survives unload), then a `keepalive` fetch
   * when it is missing or reports failure. Every failure is swallowed: the
   * events are simply discarded, nothing is retried and nothing is shown to the
   * visitor (Req 2.12, 14.5).
   */
  flush(): void {
    if (!this.enabled || this.queue.length === 0) {
      return;
    }
    // Take the events out first: the queue must end up empty on every path,
    // including one where serializing or sending throws.
    const events = this.queue.splice(0, this.queue.length);
    try {
      const batch: EngagementBatch = { sessionId: this.sessionId, events };
      const body = JSON.stringify(batch);
      if (!this.trySend(body)) {
        this.trySendFallback(body);
      }
    } catch {
      // Discarded, as above.
    }
  }

  /** Builds one payload event, carrying only the permitted fields (Req 3.2). */
  private buildEvent(
    type: EngagementEventType,
    targetId?: string,
  ): EngagementEventInput {
    this.eventCounter += 1;
    const event: EngagementEventInput = {
      eventId: `${this.sessionId}:${this.eventCounter}`,
      type,
      clientTimestamp: new Date(this.deps.now()).toISOString(),
      screenWidthBucket: screenWidthBucketOf(safeWidth(this.deps)),
    };
    if (targetId !== undefined) {
      event.targetId = targetId;
    }
    if (type === EngagementEventType.PAGE_VIEW) {
      event.referrerOrigin = safeReferrerOrigin(this.deps);
    }
    return event;
  }

  /** `navigator.sendBeacon`, treating a throw as a failed send. */
  private trySend(body: string): boolean {
    try {
      return this.deps.send(this.url, body) === true;
    } catch {
      return false;
    }
  }

  /** `fetch(..., { keepalive: true })` fallback; failures are swallowed. */
  private trySendFallback(body: string): void {
    try {
      if (this.deps.sendFallback) {
        this.deps.sendFallback(this.url, body);
        return;
      }
      keepaliveFetch(this.url, body);
    } catch {
      // Nothing further to try.
    }
  }
}

/** Trims trailing slashes so the send URL never contains a double slash. */
function normalizeBaseUrl(apiBaseUrl: string | undefined): string {
  return (apiBaseUrl ?? "").replace(/\/+$/, "");
}

/** Reads the privacy signal defensively; an unreadable signal counts as off. */
function readPrivacyControl(deps: SiteTrackerDeps): boolean {
  try {
    return deps.privacyControlEnabled() === true;
  } catch {
    return false;
  }
}

/**
 * Returns the session id held under {@link SITE_TRACKER_SESSION_KEY},
 * generating and storing one on first use. A storage that throws (Safari's
 * private mode, a blocked-storage policy) still yields a usable in-memory id
 * for this tracker instance — no cookie fallback is ever attempted (Req 3.10).
 */
function resolveSessionId(deps: SiteTrackerDeps): string {
  let stored: string | null = null;
  try {
    stored = deps.sessionStore.getItem(SITE_TRACKER_SESSION_KEY);
  } catch {
    stored = null;
  }
  if (stored) {
    return stored;
  }
  let generated: string;
  try {
    generated = deps.randomId();
  } catch {
    generated = "";
  }
  if (!generated) {
    // Last resort so event ids stay unique within this session.
    generated = `s-${Math.random().toString(36).slice(2)}`;
  }
  try {
    deps.sessionStore.setItem(SITE_TRACKER_SESSION_KEY, generated);
  } catch {
    // An unwritable store just means the id lives for this instance only.
  }
  return generated;
}

/** Reads the viewport width defensively; an unreadable width buckets as `sm`. */
function safeWidth(deps: SiteTrackerDeps): number {
  try {
    return deps.screenWidth();
  } catch {
    return 0;
  }
}

/** Reads the referrer origin defensively; unknown reads as direct (`""`). */
function safeReferrerOrigin(deps: SiteTrackerDeps): string {
  try {
    return deps.referrerOrigin?.() ?? "";
  } catch {
    return "";
  }
}

/**
 * Posts `body` with `keepalive` so the request outlives the page. Sent as
 * `text/plain` to match what `sendBeacon` would send, which also keeps the
 * request CORS-simple (no preflight on the public ingest route).
 */
function keepaliveFetch(url: string, body: string): void {
  const fetchImpl = (globalThis as { fetch?: typeof fetch }).fetch;
  if (typeof fetchImpl !== "function") {
    return;
  }
  void fetchImpl(url, {
    method: "POST",
    body,
    keepalive: true,
    headers: { "Content-Type": "text/plain;charset=UTF-8" },
  }).catch(() => {
    // Discarded: a failed send is never surfaced to the visitor.
  });
}
